import { CardTitle } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { useVehicleAnalyticsStore } from '@/store/vehicleAnalytics.store'
import { formatPLN } from '@/utils/currency'

import { CATEGORY_COLORS } from './chartTheme'

interface MetricProps {
  title: string;
  value: string;
  hint?: string;
  color?: string;
}

function Metric({ title, value, hint, color }: MetricProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-foreground/60 flex items-center gap-2">
          {color && <span className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-foreground/50 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  )
}

export function VehicleMetrics() {
  const { t } = useTranslation()
  const { totals, reports } = useVehicleAnalyticsStore()

  const pricePerLiter = totals.fuelLiters > 0 ? totals.fuel / totals.fuelLiters : 0

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
      <Metric
        title={t('vehicle_analytics.total')}
        value={`${formatPLN(totals.all)} PLN`}
        hint={t('vehicle_analytics.reports_count', { count: reports.length })}
      />
      <Metric
        title={t('vehicle_analytics.categories.advance')}
        value={`${formatPLN(totals.advance)} PLN`}
        hint={t('vehicle_analytics.advance_hint')}
        color={CATEGORY_COLORS.advance}
      />
      <Metric
        title={t('vehicle_analytics.categories.paidRoads')}
        value={`${formatPLN(totals.paidRoads)} PLN`}
        color={CATEGORY_COLORS.paidRoads}
      />
      <Metric
        title={t('vehicle_analytics.categories.fuel')}
        value={`${formatPLN(totals.fuel)} PLN`}
        hint={t('vehicle_analytics.fuel_hint')}
        color={CATEGORY_COLORS.fuel}
      />
      <Metric
        title={t('vehicle_analytics.fuel_liters')}
        value={`${formatPLN(totals.fuelLiters)} L`}
        hint={pricePerLiter > 0 ? t('vehicle_analytics.price_per_liter', { value: formatPLN(pricePerLiter) }) : undefined}
      />
    </div>
  )
}
